import { MouseEventHandler, ReactNode, ReactPortal, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { motion, AnimatePresence } from 'motion/react';
import type { Variants } from 'motion/react';

import classNames from 'classnames';

import * as styles from './Modal.css';
import { atoms } from '@ui-lib/css';

/**
 * Locks scroll on document body on mount
 *
 * Restores scroll lock on umount
 */
const useScrollLock = () => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  });
};

const Backdrop = ({
  position,
  blurBackground,
  children,
  onClick,
  onMouseDown,
  className,
}: {
  position?: 'bottom' | 'center';
  blurBackground?: boolean;
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onMouseDown?: MouseEventHandler<HTMLDivElement>;
  className?: string;
}) => {
  useScrollLock();
  return (
    <motion.div
      onClick={onClick}
      onMouseDown={(e) => {
        onMouseDown?.(e);
        e.stopPropagation();
      }}
      className={classNames(
        styles.backdrop({ blurBackground }),
        position === 'bottom' ? styles.bottomPosition : styles.centerPosition,
        atoms({
          zIndex: 'modal',
        }),
        className
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        ease: 'easeInOut',
        duration: 0.1,
      }}
    >
      {children}
    </motion.div>
  );
};

export const ModalContainer = ({ children }: { children: ReactNode }): JSX.Element => (
  <AnimatePresence initial={false} mode="wait">
    {children}
  </AnimatePresence>
);

const slide: Variants = {
  hidden: {
    y: '100%',
    opacity: 0,
    scale: 1,
  },
  visible: {
    y: '0%',
    opacity: 1,
    scale: 1,
    transition: {
      duration: 200,
      type: 'spring',
      damping: 37,
      stiffness: 500,
    },
  },
  exit: {
    y: '100%',
    opacity: 0,
    scale: 1,
  },
};

const scale: Variants = {
  hidden: {
    scale: 0.95,
    opacity: 0,
    y: '0%',
  },
  visible: {
    scale: 1,
    opacity: 1,
    y: '0%',
    transition: {
      duration: 200,
      type: 'spring',
      damping: 37,
      stiffness: 500,
    },
  },
  exit: {
    scale: 0.95,
    opacity: 0,
    y: '0%',
  },
};

export const Modal = ({
  open,
  children,
  handleClose,
  position,
  blurBackground,
  className,
}: {
  open: boolean;
  children: ReactNode;
  handleClose?: () => void;
  position?: 'bottom' | 'center';
  blurBackground?: boolean;
  className?: string;
}): ReactPortal | null => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [isMouseDownInside, setIsMouseDownInside] = useState(false);

  const handleMouseDown = (event: React.MouseEvent) => {
    if (modalRef.current?.contains(event.target as Node)) {
      setIsMouseDownInside(true);
    } else {
      setIsMouseDownInside(false);
    }
  };

  const handleBackdropClick = () => {
    if (!isMouseDownInside) {
      handleClose?.();
    }
  };

  // Call handleClose on unmount
  useEffect(() => {
    return handleClose;
  }, []);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <ModalContainer>
      {open && (
        <Backdrop
          position={position}
          onClick={handleBackdropClick}
          onMouseDown={handleMouseDown}
          blurBackground={blurBackground}
          className={className}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            variants={position === 'bottom' ? slide : scale}
            className={position === 'bottom' ? styles.fullWidth : undefined}
            initial="hidden"
            animate="visible"
            exit="exit"
            ref={modalRef}
          >
            {children}
          </motion.div>
        </Backdrop>
      )}
    </ModalContainer>,
    document.body
  );
};
